import { useEffect, useRef, useState } from 'react'
import { Send, Trash2, Bot, User as UserIcon } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { ChatMessage } from '../types'

interface ChatPanelProps {
  messages: ChatMessage[]
  streamingMessageId: string | null
  streamingText: string
  isSending: boolean
  onSend: (content: string) => Promise<void>
  onClear: () => Promise<void>
  error?: string | null
}

export default function ChatPanel({
  messages,
  streamingMessageId,
  streamingText,
  isSending,
  onSend,
  onClear,
  error,
}: ChatPanelProps) {
  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, streamingText])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const value = input.trim()
    if (!value || isSending) return
    setInput('')
    await onSend(value)
  }

  const renderBubble = (msg: ChatMessage) => {
    const isUser = msg.role === 'user'
    return (
      <div key={msg.messageId} className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
        {!isUser && (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: '#818CF815' }}
          >
            <Bot className="w-4 h-4" style={{ color: '#818CF8' }} />
          </div>
        )}
        <div
          className={`max-w-[80%] rounded-2xl px-4 py-3 ${isUser ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
          style={{
            background: isUser ? 'rgba(129, 140, 248, 0.15)' : 'rgba(255, 255, 255, 0.04)',
            border: isUser ? '1px solid rgba(129, 140, 248, 0.25)' : '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <div className="text-sm leading-relaxed" style={{ color: '#F1F5F9' }}>
            {isUser ? (
              <p className="whitespace-pre-wrap">{msg.content}</p>
            ) : (
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            )}
          </div>
          {msg.citations && msg.citations.length > 0 && (
            <div className="mt-2 pt-2 border-t flex flex-wrap gap-1" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              {msg.citations.map((c) => (
                <span
                  key={c.index}
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(129, 140, 248, 0.1)', color: '#A5B4FC' }}
                  title={`score: ${c.score.toFixed(3)}`}
                >
                  [{c.index}]
                </span>
              ))}
            </div>
          )}
        </div>
        {isUser && (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: '#10B98115' }}
          >
            <UserIcon className="w-4 h-4" style={{ color: '#10B981' }} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="glass-card flex flex-col h-[70vh]">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3 border-b"
        style={{ borderColor: 'rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" style={{ color: '#818CF8' }} />
          <h3 className="font-semibold" style={{ color: '#F1F5F9' }}>Chat con el material</h3>
        </div>
        {messages.length > 0 && (
          <button
            onClick={onClear}
            className="text-xs flex items-center gap-1 opacity-70 hover:opacity-100"
            style={{ color: '#EF4444' }}
          >
            <Trash2 className="w-3 h-3" />
            Limpiar
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {messages.length === 0 && !streamingMessageId && (
          <div className="text-center py-12 opacity-60">
            <Bot className="w-8 h-8 mx-auto mb-3" style={{ color: '#818CF8' }} />
            <p className="text-sm" style={{ color: '#94A3B8' }}>
              Pregunta lo que quieras sobre el material.
            </p>
            <p className="text-xs mt-1" style={{ color: '#64748B' }}>
              Las respuestas incluyen citas al contenido fuente.
            </p>
          </div>
        )}

        {messages.map(renderBubble)}

        {streamingMessageId && (
          <div className="flex gap-3 justify-start">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: '#818CF815' }}
            >
              <Bot className="w-4 h-4" style={{ color: '#818CF8' }} />
            </div>
            <div
              className="max-w-[80%] rounded-2xl rounded-tl-sm px-4 py-3"
              style={{
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <div className="text-sm leading-relaxed" style={{ color: '#F1F5F9' }}>
                {streamingText
                  ? <ReactMarkdown>{streamingText}</ReactMarkdown>
                  : <span className="opacity-60">Pensando…</span>}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            className="text-sm rounded-lg px-3 py-2"
            style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#FCA5A5', border: '1px solid rgba(239, 68, 68, 0.2)' }}
          >
            {error}
          </div>
        )}
      </div>

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="p-3 border-t flex gap-2"
        style={{ borderColor: 'rgba(255,255,255,0.08)' }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu pregunta…"
          disabled={isSending}
          maxLength={2000}
          className="flex-1 px-4 py-2 rounded-xl bg-transparent outline-none text-sm disabled:opacity-50"
          style={{
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#F1F5F9',
          }}
        />
        <button
          type="submit"
          disabled={!input.trim() || isSending}
          className="px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: input.trim() && !isSending ? '#818CF8' : 'rgba(129, 140, 248, 0.3)',
            color: '#0F172A',
          }}
        >
          <Send className="w-4 h-4" />
          Enviar
        </button>
      </form>
    </div>
  )
}
